"use client";

import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";
import {
  PagePricingSchema,
  PricingRewardCard,
} from "@/sanity/pages/PagePricingTemplate";
import dotsGradientBg from "@/public/global/dots-gradient-bg.png";
import Image from "next/image";

const ease = [0.25, 0.1, 0.25, 1] as const;

function IQLogoMark() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      fill="none"
      viewBox="0 0 27 26"
    >
      <path
        fill="#fff"
        d="M26.369 19.505c-3.731 0-6.767-3.021-6.767-6.734h-6.066c0 7.042 5.757 12.771 12.833 12.771z"
      />
      <path
        fill="#fff"
        d="M12.832 0C5.756 0 0 5.728 0 12.77s5.756 12.77 12.832 12.77v-6.036c-3.73 0-6.766-3.02-6.766-6.734s3.035-6.733 6.766-6.733S19.6 9.057 19.6 12.77h6.066C25.665 5.728 19.908 0 12.832 0"
      />
    </svg>
  );
}

function RewardCard({
  card,
  index,
}: {
  card: PricingRewardCard;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease, delay: index * 0.1 }}
      className="relative overflow-hidden bg-[#f4f4f4] rounded-[0.25rem] flex flex-col lg:flex-row lg:h-[39rem]"
    >
      {/* Logo + text */}
      <div className="relative z-10 flex flex-col p-[3rem] lg:pt-[6.25rem] lg:pl-[6.75rem] lg:pb-[6.25rem] lg:pr-[4rem] lg:w-[43rem] lg:flex-shrink-0">
        <div className="bg-black rounded-[0.25rem] w-[4.875rem] h-[4.875rem] flex items-center justify-center flex-shrink-0">
          <IQLogoMark />
        </div>

        <div className="mt-[2rem] lg:mt-auto flex flex-col gap-[1.75rem]">
          {card.title && (
            <p className="mb-0 font-semibold text-[3rem] lg:text-[3.5rem] leading-[1.16] tracking-[-0.14rem] text-black">
              {card.title}
            </p>
          )}
          {card.body && (
            <div className="text-[1.875rem] lg:text-[2rem] leading-[1.5] text-black/70">
              <PortableText value={card.body} components={richTextComponent} />
            </div>
          )}
        </div>
      </div>

      {/* Image — bottom on mobile (contain), right on desktop (cover) */}
      {card.image && (
        <>
          <div className="relative w-full aspect-[4/3] lg:hidden flex-shrink-0">
            <GroqImage
              src={card.image}
              alt={card.title ?? ""}
              className="!absolute !inset-0 !w-full !h-full !object-contain"
            />
          </div>
          <div className="relative flex-1 overflow-hidden hidden lg:block">
            <GroqImage
              src={card.image}
              alt={card.title ?? ""}
              className="!absolute !inset-0 !w-full !h-full !object-cover"
            />
          </div>
        </>
      )}
    </motion.div>
  );
}

export default function RewardsSection({ data }: { data: PagePricingSchema }) {
  const cards = data?.rewardCards ?? [];

  return (
    <section className="w-full bg-white py-[8rem] lg:py-[20rem] relative">
      <div className="absolute w-[calc(100%+50rem)] lg:w-full h-[48rem] top-0 left-1/2 -translate-x-1/2 mix-blend-exclusion pointer-events-none select-none">
        <Image fill alt="Background pattern" src={dotsGradientBg} />
      </div>
      <div className="mx-auto w-[min(170.5rem,calc(100%-4rem))] lg:w-[min(170.5rem,calc(100%-8rem))] flex flex-col gap-[8rem]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[1.5rem] text-center"
        >
          {data?.rewardsChip && (
            <p className="font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.rewardsChip}
            </p>
          )}
          {data?.rewardsTitle && (
            <div className="flex flex-col items-center gap-[1.5rem]">
              <div
                className="richtext flex flex-col gap-[2rem]
                              "
              >
                <PortableText
                  value={data.rewardsTitle}
                  components={richTextComponent}
                />
              </div>
              {/* <div className="richtext prose-p:text-black/70 prose-p:text-[2rem] prose-headings:hidden max-w-[60rem]">
                <PortableText
                  value={data.rewardsTitle}
                  components={richTextComponent}
                />
              </div> */}
            </div>
          )}
        </motion.div>

        {/* Cards */}
        {cards.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-[3rem]">
            {cards.map((card, i) => (
              <RewardCard key={card._key ?? i} card={card} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
